import {
  all,
  delay,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { Channel, channel } from 'redux-saga';

import { safeApply } from './errors';
import {
  createTaskAction,
  fetchTaskCommentsAction,
  fetchTaskLogsAction,
  fetchTasksAction,
  selectTaskAction,
  sendTaskCommentAction,
  updateTaskAction,
  uploadTaskAttachmentsAction,
  WS_ADD_TASK_COMMENT_IN_LIST,
  WS_ADD_TASK_IN_LIST,
  WS_ADD_TASK_LOG_IN_LIST,
  WS_DELETE_TASK_FROM_LIST,
  WS_UPDATE_ACTIVE_TASK,
  WS_UPDATE_TASK_IN_LIST,
  createTaskRelationAction,
  removeTaskRelationAction,
  searchRelationEntitiesAction,
  deleteTaskAttachmentAction,
  updateTaskInListAction,
  changeViewAction,
} from '../actions/tasks';
import {
  addTask,
  addTaskCommentInList,
  addTaskLogInList,
  deleteTaskFromList,
  removeActiveTask,
  setActiveTask,
  setEntitiesSearchResults,
  setFilters,
  setIsCreatingNewTask,
  setIsFirstTimeLoading,
  setIsLoadingActiveTask,
  setIsLoadingEntities,
  setIsLoadingTasksList,
  setIsNextCommentsPageLoading,
  setIsNextPageLoading,
  setIsOpenedActiveTask,
  setIsSendingTaskComment,
  setIsShownCreateNewTaskPopup,
  setIsTaskCommentsLoading,
  setIsTaskLogsLoading,
  setIsUpdatingActiveTask,
  setSelectedDate,
  setTaskCommentBody,
  setTaskComments,
  setTaskCommentsMeta,
  setTaskCommentsPage,
  setTaskLogs,
  setTaskLogsMeta,
  setTaskLogsPage,
  setTasks,
  setTasksListMeta,
  setTasksPage,
  setTasksTemplates,
  setView,
  updateActiveTaskInfo as updateActiveTaskAction,
  updateTaskInList,
} from '../reducers/tasks';
import { safeCall } from './errors';
import fetchData from '../../api/fetchData';
import {
  generateTaskBackendParams,
  serializeTask,
  serializeTaskLogs,
  serializeTasks,
  serializeMetaInfo,
  serializeTaskEntities,
  serializeTicketEntities,
  serializeOrderEntities,
  serializeTaskComments,
} from '../serializers/tasks';
import { serializeTaskTemplates } from '../serializers/templates';
import {
  activeTaskIdSelector,
  prevActiveTaskIdSelector,
  prevTasksFiltersSelector,
  selectedDateSelector,
  taskCommentsListMetaSelector,
  taskCommentsSelector,
  taskLogsListMetaSelector,
  taskLogsSelector,
  tasksFiltersSelector,
  tasksListMetaSelector,
  tasksSelector,
  viewSelector,
} from '../selectors/tasks';
import { ROUTE } from '@constants/pages';
import { history } from '../';
import { buildFilters, generateQueryFetchTasks } from '@utils/tasksUtils';
import { mergeEntities } from '@utils/mergeEntities';

import ActiveTaskChannelHandler from './socketHandlers/tasks/ActiveTaskChannelHandler';
import WebSocketConnector from '../../api/sockets/WebSocketConnector';
import {
  SOCKET_ACTIVE_TASK_CHANNEL,
  SOCKET_ACTIVE_TASK_COMMENT_CHANNEL,
  SOCKET_TASK_CHANNEL,
} from '@constants/socketChannels';
import {
  currentManagerIdSelector,
  sessionIdSelector,
} from '../selectors/general';
import TaskChannelHandler from './socketHandlers/tasks/TaskChannelHandler';
import { RELATION_ENTITY_TYPES } from '@components/CRM/Tasks/TaskRelations/constants';
import { TICKETS_API_URL } from './tickets';
import ActiveTaskCommentsChannelHandler from './socketHandlers/tasks/ActiveTaskCommentsChannelHandler';
import { normalizeHTMLString } from './utils/utils';
import { ITask } from 'types/types';
import { VIEWS } from '@constants/tasks';
import { endOfWeek, startOfWeek } from 'date-fns';

const ORDERS_API_URL = `${process.env.API_URL}/orders`;
const TASKS_API_URL = `${process.env.API_URL}/tasks`;
const TASK_TEAMPLATES_API_URL = `${process.env.API_URL}/task_templates`;

function* watchSocketActiveTaskMessage(webSocketConnector, identifier) {
  const socketChannel: Channel<Record<string, unknown>> = channel();
  const activeTicketChannelHandler = new ActiveTaskChannelHandler(
    socketChannel
  );
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* watchSocketActiveTaskCommentsMessage(webSocketConnector, identifier) {
  const socketChannel: Channel<Record<string, unknown>> = channel();
  const activeTicketChannelHandler = new ActiveTaskCommentsChannelHandler(
    socketChannel
  );
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeActiveTaskSocket({ payload: activeTask }) {
  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  const identifier = {
    channel: SOCKET_ACTIVE_TASK_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
    task_id: activeTask.id,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchSocketActiveTaskMessage, webSocketConnector, identifier);
}

function* subscribeActiveTaskCommentsSocket({ payload: activeTask }) {
  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  const identifier = {
    channel: SOCKET_ACTIVE_TASK_COMMENT_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
    task_id: activeTask.id,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(
    watchSocketActiveTaskCommentsMessage,
    webSocketConnector,
    identifier
  );
}

function* unsubscribeActiveTaskCommentsSocket() {
  const prevActiveTaskId = yield select(prevActiveTaskIdSelector);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  if (!prevActiveTaskId) {
    return;
  }

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel: SOCKET_ACTIVE_TASK_COMMENT_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
    task_id: prevActiveTaskId,
  });
}

function* unsubscribeActiveTaskSocket() {
  const prevActiveTaskId = yield select(prevActiveTaskIdSelector);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  if (!prevActiveTaskId) {
    return;
  }

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel: SOCKET_ACTIVE_TASK_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
    task_id: prevActiveTaskId,
  });
}

function* watchTasksSocketMessage(webSocketConnector, identifier) {
  const socketChannel: Channel<Record<string, unknown>> = channel();
  const ticketChannelHandler = new TaskChannelHandler(socketChannel);
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: ticketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeTasksSocket() {
  const filters = yield select(tasksFiltersSelector);
  const prevFilters = yield select(prevTasksFiltersSelector);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_TASK_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
    filters: buildFilters(filters),
  };

  const prevIdentifier = {
    ...identifier,
    filters: buildFilters(prevFilters),
  };

  webSocketConnector.unsubscribeChannel(prevIdentifier);
  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchTasksSocketMessage, webSocketConnector, identifier);
}

function* createTask({ payload }) {
  try {
    const { attachments } = payload;

    const formData = new FormData();

    const requestData = generateTaskBackendParams(payload);

    for (let param in requestData) {
      // теги добавляем отдельно
      if (param === 'tag_ids') continue;
      formData.append(param, requestData[param]);
    }

    if (requestData.tag_ids?.length) {
      requestData.tag_ids.forEach(tagId => {
        formData.append('tag_ids[]', tagId);
      });
    }

    if (attachments.length) {
      Array.from(attachments).forEach(file => {
        if (file instanceof File) formData.append('attachments[]', file);
      });
    }

    yield put(setIsCreatingNewTask(true));
    const { task } = yield safeCall(fetchData, TASKS_API_URL, {
      method: 'POST',
      body: formData,
    });
    yield put(setIsCreatingNewTask(false));
    yield put(setIsShownCreateNewTaskPopup(false));

    yield delay(300);

    if (!task || !task.id) return;

    const serializedTask = serializeTask(task);
    yield put(setActiveTask(serializedTask));
    yield put(setIsOpenedActiveTask(true));

    history.push(`${ROUTE.TASKS}/${serializedTask.id}`);
  } catch (error) {
    console.error('Ошибка при создании задачи: ', error);
  }
}

function* updateTask({ payload }) {
  try {
    const { taskId, startedAt, endedAt, onSuccess } = payload;

    const data = {
      started_at: startedAt,
      ended_at: endedAt,
    };

    yield safeCall(fetchData, `${TASKS_API_URL}/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });

    onSuccess();
  } catch (error) {
    console.error('Ошибка при изменении времени выполнения задачи: ', error);
  }
}

function* updateActiveTask({
  payload: { onSuccess, ...updatedFields },
}: {
  payload: Record<string, any>;
}) {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);

    const data = Object.entries(updatedFields).reduce((acc, [name, value]) => {
      acc[name] = value;
      return acc;
    }, {});

    yield put(setIsUpdatingActiveTask(true));
    yield safeCall(fetchData, `${TASKS_API_URL}/${activeTaskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
    yield put(setIsUpdatingActiveTask(false));

    onSuccess?.();
  } catch (error) {
    console.error('Ошибка при изменении задачи: ', error);
  }
}

function* uploadActiveTaskAttachments({ payload }) {
  try {
    const { attachments } = payload;
    const activeTaskId = yield select(activeTaskIdSelector);

    const formData = new FormData();

    if (attachments && attachments.length > 0) {
      Array.from(attachments).forEach(file => {
        if (file instanceof File) formData.append('attachments[]', file);
      });

      yield put(setIsUpdatingActiveTask(true));
      yield safeCall(fetchData, `${TASKS_API_URL}/${activeTaskId}`, {
        method: 'PUT',
        body: formData,
      });
      yield put(setIsUpdatingActiveTask(false));
    }
  } catch (error) {
    console.error('Ошибка при загрузке вложений в активной задаче: ', error);
  }
}

function* deleteActiveTaskAttachment({ payload }) {
  try {
    const { deletedAttachment } = payload;
    const activeTaskId = yield select(activeTaskIdSelector);

    if (deletedAttachment) {
      yield put(setIsUpdatingActiveTask(true));
      yield safeCall(
        fetchData,
        `${TASKS_API_URL}/${activeTaskId}/attachment/${deletedAttachment}`,
        {
          method: 'DELETE',
        }
      );
      yield put(setIsUpdatingActiveTask(false));
    }
  } catch (error) {
    console.error('Ошибка при удалении вложения в активной задаче: ', error);
  }
}

function* fetchTask({ payload: { id } }) {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    if (activeTaskId) {
      yield put(removeActiveTask());
    }

    yield put(setIsLoadingActiveTask(true));
    yield put(setIsOpenedActiveTask(true));
    const { task } = yield safeCall(fetchData, `${TASKS_API_URL}/${id}`);
    const serializedTask = serializeTask(task);
    yield put(setActiveTask(serializedTask));
    yield put(setIsLoadingActiveTask(false));

    history.push(`${ROUTE.TASKS}/${id}`);
  } catch (error) {
    console.error('Ошибка при запросе задачи: ', error);
  }
}

function* fetchTasks() {
  try {
    const view = yield select(viewSelector);

    const prevTasks = yield select(tasksSelector);
    const filters = yield select(tasksFiltersSelector);

    const { page, size } = yield select(tasksListMetaSelector);

    let queryParams;

    if (view === VIEWS.CALENDAR) {
      // у календаря нет пагинации, поэтому запрашиваем все задачи сразу (size: 1000)
      queryParams = generateQueryFetchTasks({
        size: 1000,
        page: 1,
        ...filters,
      });
    } else {
      queryParams = generateQueryFetchTasks({ size, page, ...filters });
    }

    if (page === 1 && prevTasks.length === 0) {
      yield put(setIsFirstTimeLoading(true));
    }

    yield put(setIsLoadingTasksList(true));
    yield put(setIsNextPageLoading(true));

    const { tasks, meta } = yield safeCall(
      fetchData,
      `${TASKS_API_URL}?${queryParams}`
    );

    const serializedTasks = serializeTasks(tasks);
    const serializedMeta = serializeMetaInfo(meta);

    const mergedTasks = mergeEntities<ITask>(prevTasks, serializedTasks);

    yield put(setTasks(mergedTasks));
    yield put(setTasksListMeta(serializedMeta));

    yield put(setIsNextPageLoading(false));
    yield put(setIsLoadingTasksList(false));
  } catch (error) {
    console.error('Ошибка при запросе задач: ', error);
  }
}
function* fetchTemplates() {
  try {
    const { task_templates } = yield safeCall(
      fetchData,
      TASK_TEAMPLATES_API_URL
    );

    const serializedTemplates = serializeTaskTemplates(task_templates);
    yield put(setTasksTemplates(serializedTemplates));
  } catch (error) {
    console.error('Ошибка при запросе шаблонов задач: ', error);
  }
}

function* fetchTaskLogs() {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    const prevLogs = yield select(taskLogsSelector);
    const { page, size } = yield select(taskLogsListMetaSelector);

    if (page === 1 && prevLogs.length === 0) {
      yield put(setIsTaskLogsLoading(true));
    }

    const { task_history, meta } = yield safeCall(
      fetchData,
      `${TASKS_API_URL}/${activeTaskId}/history?page=${page}&size=${size}`
    );

    const serializedLogs = serializeTaskLogs(task_history);

    const mergedLogs = mergeEntities(prevLogs, serializedLogs);
    const serializedMeta = serializeMetaInfo(meta);

    yield put(setTaskLogs(mergedLogs));
    yield put(setTaskLogsMeta(serializedMeta));

    yield put(setIsTaskLogsLoading(false));
  } catch (error) {
    yield put(setIsTaskLogsLoading(false));
    console.error('Ошибка при запросе логов задачи: ', error);
  }
}

function* createTaskRelation({
  payload,
}: ReturnType<typeof createTaskRelationAction>) {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    const { entityType, id, relationship, onSuccess } = payload;

    let requestData;

    const relation = [{ id, relationship }];

    switch (entityType) {
      case RELATION_ENTITY_TYPES.TICKET: {
        requestData = {
          relates_to_ticket: relation,
        };
        break;
      }

      case RELATION_ENTITY_TYPES.TASK: {
        requestData = {
          relates_to_task: relation,
        };
        break;
      }

      case RELATION_ENTITY_TYPES.ORDER: {
        requestData = {
          relates_to_order: relation,
        };
        break;
      }

      default: {
        requestData = {
          relates_to_task: relation,
        };
      }
    }

    yield put(setIsUpdatingActiveTask(true));

    yield safeCall(fetchData, `${TASKS_API_URL}/${activeTaskId}/relation`, {
      method: 'POST',
      body: JSON.stringify(requestData),
      headers: { 'Content-Type': 'application/json' },
    });

    yield put(setIsUpdatingActiveTask(false));

    onSuccess();
  } catch (error) {
    yield put(setIsUpdatingActiveTask(false));
    console.error('Ошибка при создании связи задачи: ', error);
  }
}

function* removeTaskRelation({
  payload,
}: ReturnType<typeof removeTaskRelationAction>) {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    const { relationId } = payload;

    yield put(setIsUpdatingActiveTask(true));

    yield safeCall(
      fetchData,
      `${TASKS_API_URL}/${activeTaskId}/relation/${relationId}`,
      {
        method: 'DELETE',
      }
    );

    yield put(setIsUpdatingActiveTask(false));
  } catch (error) {
    yield put(setIsUpdatingActiveTask(false));
    console.error('Ошибка при удалении связи задачи: ', error);
  }
}

function* searchRelationEntities({
  payload,
}: ReturnType<typeof searchRelationEntitiesAction>) {
  try {
    const { entityType, query } = payload;

    yield put(setIsLoadingEntities(true));

    let serializedEntities;

    switch (entityType) {
      case RELATION_ENTITY_TYPES.TASK:
        const tasksResponse = yield safeCall(
          fetchData,
          `${TASKS_API_URL}/search?q=${query}&size=10`
        );
        serializedEntities = serializeTaskEntities(tasksResponse.tasks);
        break;

      case RELATION_ENTITY_TYPES.TICKET:
        const ticketsResponse = yield safeCall(
          fetchData,
          `${TICKETS_API_URL}/search?q=${query}&size=10`
        );
        serializedEntities = serializeTicketEntities(ticketsResponse.tickets);
        break;

      case RELATION_ENTITY_TYPES.ORDER:
        const ordersResponse = yield safeCall(
          fetchData,
          `${ORDERS_API_URL}/search?q=${query}&size=10`
        );
        serializedEntities = serializeOrderEntities(ordersResponse.orders);
        break;
    }

    yield put(setEntitiesSearchResults(serializedEntities));

    yield put(setIsLoadingEntities(false));
  } catch (error) {
    yield put(setIsLoadingEntities(false));
    console.error('Ошибка при запросе сущностей для создания связи: ', error);
  }
}

function* fetchTaskComments() {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    const prevComments = yield select(taskCommentsSelector);
    const { page, size } = yield select(taskCommentsListMetaSelector);

    if (page === 1 && prevComments.length === 0) {
      yield put(setIsTaskCommentsLoading(true));
    }

    yield put(setIsNextCommentsPageLoading(true));

    const { task_comments, meta } = yield safeCall(
      fetchData,
      `${TASKS_API_URL}/${activeTaskId}/task_comments?page=${page}&size=${size}`
    );

    const serializedComments = serializeTaskComments(task_comments);

    const mergedComments = mergeEntities(serializedComments, prevComments);
    const serializedMeta = serializeMetaInfo(meta);

    yield put(setTaskComments(mergedComments));
    yield put(setTaskCommentsMeta(serializedMeta));
    yield put(setIsNextCommentsPageLoading(false));

    yield put(setIsTaskCommentsLoading(false));
  } catch (error) {
    yield put(setIsTaskCommentsLoading(false));
    console.error('Ошибка при запросе комментариев задачи: ', error);
  }
}

function* sendTaskComment({ payload }) {
  try {
    const activeTaskId = yield select(activeTaskIdSelector);
    yield put(setIsSendingTaskComment(true));

    yield safeCall(
      fetchData,
      `${TASKS_API_URL}/${activeTaskId}/task_comments`,
      {
        method: 'POST',
        body: JSON.stringify({
          ...payload,
          task_id: Number(activeTaskId),
          text: normalizeHTMLString(payload.text),
        }),
        headers: { 'Content-Type': 'application/json' },
      }
    );

    yield put(setTaskCommentBody(''));
    yield put(setIsSendingTaskComment(false));
  } catch (error) {
    yield put(setIsTaskLogsLoading(false));
    console.error('Ошибка при отправке комментария задачи: ', error);
  }
}

function* changeView({ payload }) {
  try {
    const selectedDate = yield select(selectedDateSelector);
    const managerId = yield select(currentManagerIdSelector);

    yield put(setView(payload));

    if (payload === VIEWS.CALENDAR) {
      // в календаре загружаем только задачи, которые находятся на неделе выбранной даты selectedDate
      const weekStartISO = startOfWeek(selectedDate, {
        weekStartsOn: 1,
      }).toISOString(); // понедельник
      const weekEndISO = endOfWeek(selectedDate, {
        weekStartsOn: 1,
      }).toISOString(); // воскресенье

      yield put(
        setFilters({
          assigneeId: [managerId],
          startedAtFrom: weekStartISO,
          endedAtTo: weekEndISO,
        })
      );
    }
  } catch (error) {
    yield put(setIsTaskLogsLoading(false));
    console.error('Ошибка при изменении вида списка задач: ', error);
  }
}

function* wsUpdateActiveTask({ payload: { activeTask } }) {
  yield put(updateActiveTaskAction(activeTask));
}

function* wsAddTaskLogInList({ payload: { log } }) {
  const logs = yield select(taskLogsSelector);

  // добавляем в список только в том случае, если логи уже были запрошены ранее
  if (logs.length) {
    yield put(addTaskLogInList(log));
  }
}

function* wsAddTaskInList({ payload: { task } }) {
  yield put(addTask(task));
}

function* wsUpdateTaskInList({ payload: { task } }) {
  yield put(updateTaskInList(task));
}

function* wsDeleteTaskFromList({ payload: { taskId } }) {
  yield put(deleteTaskFromList(taskId));
}

function* wsAddCommentInList({ payload: { comment } }) {
  console.log('wsAddCommentInList', comment);
  yield put(addTaskCommentInList(comment));
}

export default function* () {
  yield all([
    safeCall(fetchTemplates),
    takeLatest(createTaskAction, safeApply(createTask)),
    takeLatest(
      [fetchTasksAction, setTasksPage, setFilters, setSelectedDate],
      safeApply(fetchTasks)
    ),
    takeLatest(updateTaskInListAction, safeApply(updateTask)),
    takeLatest(setTasks, safeApply(subscribeTasksSocket)),
    takeLatest(WS_ADD_TASK_IN_LIST, safeApply(wsAddTaskInList)),
    takeLatest(WS_UPDATE_TASK_IN_LIST, safeApply(wsUpdateTaskInList)),
    takeLatest(WS_DELETE_TASK_FROM_LIST, safeApply(wsDeleteTaskFromList)),

    // Активная задача
    takeLatest(selectTaskAction, safeApply(fetchTask)),
    takeLatest(updateTaskAction, safeApply(updateActiveTask)),
    takeLatest(
      uploadTaskAttachmentsAction,
      safeApply(uploadActiveTaskAttachments)
    ),
    takeLatest(
      deleteTaskAttachmentAction,
      safeApply(deleteActiveTaskAttachment)
    ),
    takeLatest(setActiveTask, safeApply(subscribeActiveTaskSocket)),
    takeLatest(
      [selectTaskAction, removeActiveTask],
      safeApply(unsubscribeActiveTaskSocket)
    ),

    takeLatest(searchRelationEntitiesAction, safeApply(searchRelationEntities)),
    takeLatest(createTaskRelationAction, safeApply(createTaskRelation)),
    takeLatest(removeTaskRelationAction, safeApply(removeTaskRelation)),

    takeLatest(WS_UPDATE_ACTIVE_TASK, safeApply(wsUpdateActiveTask)),

    // Логи в активной задаче
    takeLatest(
      [fetchTaskLogsAction, setTaskLogsPage],
      safeApply(fetchTaskLogs)
    ),
    takeLatest(WS_ADD_TASK_LOG_IN_LIST, safeApply(wsAddTaskLogInList)),

    // Комменты в активной задаче
    takeLatest(
      [fetchTaskCommentsAction, setTaskCommentsPage],
      safeApply(fetchTaskComments)
    ),
    takeLatest(sendTaskCommentAction, safeApply(sendTaskComment)),
    takeLatest(setActiveTask, safeApply(subscribeActiveTaskCommentsSocket)),
    takeLatest(
      [selectTaskAction, removeActiveTask],
      safeApply(unsubscribeActiveTaskCommentsSocket)
    ),
    takeLatest(WS_ADD_TASK_COMMENT_IN_LIST, safeApply(wsAddCommentInList)),

    takeEvery(changeViewAction, safeApply(changeView)),
  ]);
}
